import { useRef, useState, useCallback, useMemo, useEffect } from 'react'
import { createRoot, Root } from 'react-dom/client'
import HotTable, { HotTableProps } from '@handsontable/react'
import Handsontable from 'handsontable'
import { registerAllModules } from 'handsontable/registry'
import { ColumnSettings } from 'handsontable/settings'
import { useParams } from 'react-router-dom'
import { LICENSE_KEY } from '@global/constants/handsontable'
import 'handsontable/dist/handsontable.full.css'
import {
  useUpdateFieldTranslationReview,
  useUpdateListValueTranslationReview,
} from '@global/hooks/mutations/taxonomy-translations'
import { useInvalidateTaxonomyTranslationFieldTranslations } from '@global/hooks/queries/taxonomy-translations'
import { Fuzzy } from '@utils/fuse-search'
import { ColumnSelectorButton, FilterSelectorButton } from './components'
import { useFilterForm } from './components/filter-selector-button/useFilterForm'
import { statusColumnRenderer } from './components/table-column-renderer/status-column-renderer'
import { translatedFieldColumnRender } from './components/table-column-renderer/translated-field-column-render'
import { translatedListValueColumnRenderer } from './components/table-column-renderer/translated-list-value-column-render'
import { PREDEFINED_ROW } from './constants'
import { cssTable } from './styles'
import { TableData, TableDataChild } from './useTableData'
import { FilterOption, TaxonomyTranslationInput } from '../../../types'
import { formatInputNames, trackItemReviewAction } from '@taxonomy-translation/utils'
import { SearchField } from '@taxonomy-translation/components'
import { useSearch } from '@taxonomy-translation/components/search-field/useSearch'
import { ColumnDataGetterSetterFunction } from 'handsontable/common'
import { HeaderTooltip } from './components/header-tooltip'
import { t } from '@lingui/core/macro'

interface TranslationDashboardProps {
  data: TableData[]
  inputs: TaxonomyTranslationInput[]
}

registerAllModules()

const applyFilters = (dataToFilter: TableData[], filters: FilterOption[]) => {
  if (!filters.length) {
    return dataToFilter
  }

  const applyFilter = (r: TableData | TableDataChild) => {
    return filters.some(({ field, value }) => {
      if (!r[field]) {
        return false
      }

      if (field === 'confidence') {
        if (value === 'not_confidence') {
          return r[field] < 0.8
        }

        // Full confidence
        return r[field] === 1
      }

      return r[field] === value
    })
  }

  const filteredTableChildrensData = dataToFilter.reduce<TableData[]>((acc, row) => {
    if (row.__children !== undefined && row.__children.length > 0) {
      const filteredChildren = row.__children.filter(applyFilter)

      acc.push({ ...row, __children: filteredChildren })
    } else {
      acc.push(row)
    }

    return acc
  }, [])

  return filteredTableChildrensData.filter(applyFilter)
}

interface TranslationDashboardRouterParams {
  id: string
}

export const TranslationDashboard = ({ data, inputs }: TranslationDashboardProps) => {
  const { id } = useParams<TranslationDashboardRouterParams>()

  const hotTableRef = useRef<HotTable>(null)
  const headerRootsRef = useRef<Map<HTMLTableCellElement, Root>>(new Map())
  const { searchTerm, debouncedSearchTerm: searchQuery, onSearchChange } = useSearch()
  const channels = useMemo(
    () => [...new Set(data.map(row => row.channel).filter((v): v is string => !!v))],
    [data],
  )

  const invalidateTranslations = useInvalidateTaxonomyTranslationFieldTranslations(id)

  const mutation = useUpdateFieldTranslationReview(id, {
    onSuccess: async () => {
      await invalidateTranslations()
      trackItemReviewAction({
        source: 'table',
        action: 'reviewed',
      })
    },
  })
  const mutationListValue = useUpdateListValueTranslationReview(id, {
    onSuccess: async () => {
      await invalidateTranslations()
      trackItemReviewAction({
        source: 'table',
        action: 'reviewed',
      })
    },
  })

  const {
    activeFilters,
    selectedFiltersInForm,
    handleClickFilter,
    removeFilter,
    handleReset,
    handleCancel,
    handleApply,
  } = useFilterForm({})

  const fuzzy = useMemo(
    () =>
      new Fuzzy(data, {
        keys: [
          'channel',
          'candidate',
          'reference',
          'translated',
          '__children.children_candidate',
          '__children.children_candidate_abbrev',
          '__children.children_translated',
          '__children.children_reference',
          '__children.children_reference_abbrev',
        ],
      }),
    [data],
  )

  const filteredData = useMemo(() => {
    let result = [...data]

    if (searchQuery) {
      result = fuzzy.search(searchQuery)
    }

    result = applyFilters(result, activeFilters)

    if (result.length === 0) {
      return PREDEFINED_ROW
    }

    return result
  }, [data, fuzzy, searchQuery, activeFilters])

  const handleApplyColumns = (selected: number[]) => {
    setHiddenColumns(selected)
  }

  const handleAfterChange = useCallback(
    (changes: Handsontable.CellChange[] | null, source: Handsontable.ChangeSource) => {
      if (!changes || source !== 'edit') {
        return
      }

      const hotInstance = hotTableRef.current?.hotInstance
      if (!hotInstance) {
        return
      }

      changes.forEach(([row, prop, , newValue]) => {
        const rowMeta = hotInstance.getSourceDataAtRow(row)
        if (isTableData(rowMeta, prop)) {
          const schema = {
            uuid: id,
            fieldId: rowMeta.id,
            expected: newValue as string,
          }

          mutation.mutate(schema)
        }

        if (isTableDataChild(rowMeta, prop)) {
          const schema = {
            uuid: id,
            fieldId: rowMeta.parent_id,
            index: rowMeta.index,
            expected: newValue as string,
          }

          mutationListValue.mutate(schema)
        }
      })
    },
    [],
  )

  const tableColumns = useMemo(
    () =>
      [
        { data: 'channel', title: t`Stream`, type: 'text', readOnly: true, width: 103 },
        {
          data: 'candidate',
          title: t`Data to be translated field name`,
          type: 'text',
          readOnly: true,
          width: 114,
        },
        {
          data: 'reference',
          title: t`Baseline field name match`,
          type: 'text',
          readOnly: true,
          width: 76,
          hidden: true,
        },
        {
          data: 'translated',
          title: t`Translated field name`,
          readOnly: true,
          renderer: translatedFieldColumnRender,
          width: 137,
        },
        {
          data: 'count',
          title: t`List value count`,
          type: 'text',
          readOnly: true,
          width: 62,
          hidden: true,
        },
        {
          data: 'children_candidate',
          title: t`Data to be translated list value`,
          type: 'text',
          readOnly: true,
          width: 95,
        },
        {
          data: 'children_candidate_abbrev',
          title: t`Data to be translated list value ABBR`,
          type: 'text',
          readOnly: true,
          width: 95,
          hidden: true,
        },
        {
          data: 'children_reference',
          title: t`Baseline list value match`,
          type: 'text',
          readOnly: true,
          width: 130,
          hidden: true,
        },
        {
          data: 'children_reference_abbrev',
          title: t`Baseline list value match ABBR`,
          type: 'text',
          readOnly: true,
          width: 95,
          hidden: true,
        },
        {
          data: 'children_translated',
          title: t`Translated list value`,
          type: 'text',
          readOnly: true,
          renderer: translatedListValueColumnRenderer,
          width: 184,
        },
        {
          data: 'confidence',
          title: t`Match confidence`,
          type: 'numeric',
          numericFormat: { pattern: '0%' },
          readOnly: true,
          width: 72,
          className: 'htLeft htMiddle',
        },
        {
          data: 'status',
          title: t`Status`,
          type: 'text',
          readOnly: true,
          columnSorting: true,
          indicator: true,
          renderer: statusColumnRenderer,
        },
      ] as ColumnSettings[],
    [],
  )

  const [hiddenColumns, setHiddenColumns] = useState<number[]>(() =>
    tableColumns
      .map((v, index) => [index, v.hidden === true] as const)
      .filter(([, hidden]) => hidden)
      .map(([index]) => index),
  )

  const candidateInputs = useMemo(() => {
    return inputs.filter(
      input => input.type === 'candidate_trafficking' || input.type === 'candidate_list_values',
    )
  }, [inputs])

  const formattedInputNames = useMemo(() => {
    return formatInputNames(candidateInputs)
  }, [candidateInputs])

  const renderHeaderTooltip = useCallback(
    (colIndex: number, headerElement: HTMLTableCellElement, inputNames: string[]) => {
      try {
        const isCandidate =
          tableColumns[colIndex]?.data === 'candidate' ||
          tableColumns[colIndex]?.data === 'children_candidate'

        if (!isCandidate) {
          const existingRoot = headerRootsRef.current.get(headerElement)
          if (existingRoot) {
            existingRoot.unmount()
            headerRootsRef.current.delete(headerElement)
          }
          return
        }

        const headerContent = (
          <HeaderTooltip inputNames={inputNames}>{headerElement.innerText}</HeaderTooltip>
        )

        const existingRoot = headerRootsRef.current.get(headerElement)
        if (!existingRoot) {
          const root = createRoot(headerElement)
          headerRootsRef.current.set(headerElement, root)
          root.render(headerContent)
        } else {
          existingRoot.render(headerContent)
        }
      } catch (error) {
        console.warn('Failed to render header tooltip:', error)
      }
    },
    [],
  )

  useEffect(() => {
    return () => {
      try {
        headerRootsRef.current.forEach(root => {
          root.unmount()
        })
        headerRootsRef.current.clear()
      } catch (error) {
        console.warn('Failed to cleanup header roots:', error)
      }
    }
  }, [])

  const handleAfterGetColHeader: HotTableProps['afterGetColHeader'] = useCallback(
    (colIndex: number, TH: HTMLTableCellElement) => {
      renderHeaderTooltip(colIndex, TH, formattedInputNames)
    },
    [renderHeaderTooltip, formattedInputNames],
  )

  return (
    <div>
      <div className="mb-4 flex justify-between text-gray-900/10">
        <div className="w-[200px]">
          <SearchField onChange={onSearchChange} value={searchTerm} />
        </div>
        <div className="flex gap-2">
          <ColumnSelectorButton
            hiddenColumns={hiddenColumns}
            columns={tableColumns}
            onApply={handleApplyColumns}
          />
          <FilterSelectorButton
            channels={channels}
            activeFilters={activeFilters}
            selectedFiltersInForm={selectedFiltersInForm}
            handleClickFilter={handleClickFilter}
            removeFilter={removeFilter}
            handleReset={handleReset}
            handleCancel={handleCancel}
            handleApply={() => {
              handleApply(selectedFiltersInForm)
            }}
          />
        </div>
      </div>
      <div className="overflow-hidden h-[80vh]">
        <HotTable
          css={cssTable}
          ref={hotTableRef}
          data={filteredData}
          className="htMiddle"
          colHeaders={tableColumns.map(col => col.title) as string[]}
          rowHeaders
          rowHeaderWidth={40}
          rowHeights={65}
          afterChange={handleAfterChange}
          columns={tableColumns}
          width="100%"
          columnSorting
          height="80vh"
          stretchH="all"
          autoColumnSize
          autoRowSize
          licenseKey={LICENSE_KEY}
          nestedRows
          hiddenColumns={{
            columns: hiddenColumns,
          }}
          afterGetColHeader={handleAfterGetColHeader}
        />
      </div>
    </div>
  )
}

const isTableData = (
  _: Handsontable.RowObject,
  prop: string | number | ColumnDataGetterSetterFunction,
): _ is TableData => prop === 'translated'
const isTableDataChild = (
  _: Handsontable.RowObject,
  prop: string | number | ColumnDataGetterSetterFunction,
): _ is TableDataChild => prop === 'children_translated'
